import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PopoverService, WorkScheduleService, LoadingService } from '@services';
import { CustomFormComponent } from '@services/formBuilder/abstract-custom-form-field';
import { EntryModalComponent, RetainedSettings } from '@modules/management/pages/details/work-schedule/modals/entry-modal/entry-modal.component';
import { WorkScheduleType } from '@services/work-schedule/model';

@Component({
  selector: 'app-schedule-entries-editor',
  templateUrl: './schedule-entries-editor.component.html',
  styleUrls: ['./schedule-entries-editor.component.scss'],
  standalone: false
})
export class ScheduleEntriesEditorComponent extends CustomFormComponent implements OnInit, OnChanges {

  // Subject that owns these entries (folder, asset, team, or worker)
  @Input() subjectType: 'folder' | 'asset' | 'team' | 'worker' = 'folder';
  @Input() subjectID: number = 0;

  // The schedule ID these entries are associated with (WorkScheduleType schedule)
  @Input() scheduleID: number = 0;

  // Schedule type for display
  @Input() scheduleType: WorkScheduleType = WorkScheduleType.Work_Days;

  // Label to show above the entries section
  @Input() sectionLabel: string = '';

  // Hint text to show below the label
  @Input() hintText: string = '';

  // Whether the editor is disabled (read-only)
  @Input() disabled: boolean = false;

  // Required by CustomFormComponent
  @Output() onChanged: EventEmitter<any> = new EventEmitter<any>();
  public required: boolean = false;

  // Entries loaded from backend
  public entries: any[] = [];
  public isLoading: boolean = false;

  // For delete confirmation
  private entryToDelete: any = null;
  private deletePopoverConfig: any;

  public daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  public daysOfWeekShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  constructor(
    private translate: TranslateService,
    private workScheduleService: WorkScheduleService,
    private modalController: ModalController,
    private popoverService: PopoverService,
    private loadingService: LoadingService
  ) {
    super();

    this.deletePopoverConfig = {
      title: this.translate.instant('WORK_SCHEDULE.Delete_Entry'),
      description: this.translate.instant('WORK_SCHEDULE.Confirm_Delete_Entry'),
      onSave: () => this.confirmDeleteEntry()
    };
  }

  ngOnInit() {
    if (this.subjectID > 0) {
      this.loadEntries();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reload entries when subjectID or scheduleType changes
    if ((changes['subjectID'] || changes['scheduleType']) && this.subjectID > 0) {
      this.loadEntries();
    }
  }

  /**
   * Load entries from the backend for this subject
   */
  async loadEntries() {
    if (!this.subjectID || !this.subjectType) {
      this.entries = [];
      return;
    }

    this.isLoading = true;
    try {
      const allEntries = await this.workScheduleService.getSubjectEntries(
        this.subjectType,
        this.subjectID,
        this.scheduleType
      );
      // Group entries by entryID for display
      this.entries = this.groupEntriesByID(allEntries);
    } catch (error) {
      console.error('Failed to load subject entries:', error);
      this.entries = [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Group expanded entries by entryID so multi-day entries show as one row
   */
  private groupEntriesByID(entries: any[]): any[] {
    const entryMap = new Map<number, any>();

    for (const entry of entries) {
      const id = Number(entry.entryID);
      if (entryMap.has(id)) {
        // Add day to existing group
        const existing = entryMap.get(id);
        if (entry.dayOfWeek !== null && entry.dayOfWeek !== undefined) {
          if (!existing.daysOfWeek) {
            existing.daysOfWeek = [];
          }
          existing.daysOfWeek.push(Number(entry.dayOfWeek));
        }
      } else {
        // Create new group
        const grouped = { ...entry };
        grouped.entryID = id;
        if (entry.dayOfWeek !== null && entry.dayOfWeek !== undefined) {
          grouped.daysOfWeek = [Number(entry.dayOfWeek)];
        }
        entryMap.set(id, grouped);
      }
    }

    // Sort days within each group and convert to array
    return Array.from(entryMap.values()).map(entry => {
      if (entry.daysOfWeek) {
        entry.daysOfWeek.sort((a: number, b: number) => a - b);
      }
      return entry;
    });
  }

  /**
   * Open the entry modal to add a new entry
   */
  async addEntry(retainedSettings: RetainedSettings = null) {
    if (!this.scheduleID || !this.subjectID) {
      console.warn('Cannot add entry: scheduleID or subjectID not set');
      return;
    }

    const componentProps: Record<string, any> = {
      entry: null,
      scheduleID: this.scheduleID
    };

    if (retainedSettings) {
      componentProps.retainedSettings = retainedSettings;
    }

    const modal = await this.modalController.create({
      component: EntryModalComponent,
      componentProps,
      cssClass: 'modal-with-backdrop'
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'save' && data && Array.isArray(data) && data.length > 0) {
      await this.loadingService.enable();
      try {
        // Add subjectType and subjectID to each entry
        for (const entryData of data) {
          entryData.subjectType = this.subjectType;
          entryData.subjectID = this.subjectID;
          await this.workScheduleService.handleAddSubjectEntry(entryData);
        }
        await this.loadEntries();
        this.onChanged.emit(this.entries);
      } finally {
        this.loadingService.disable();
      }
    } else if (role === 'saveAndAddAnother' && data) {
      // Save the entry, then reopen modal with retained settings
      await this.loadingService.enable();
      try {
        const entryData = data.entry;
        if (entryData) {
          entryData.subjectType = this.subjectType;
          entryData.subjectID = this.subjectID;
          await this.workScheduleService.handleAddSubjectEntry(entryData);
          await this.loadEntries();
          this.onChanged.emit(this.entries);
        }
      } finally {
        this.loadingService.disable();
      }
      // Reopen modal with retained settings
      this.addEntry(data.retainedSettings);
    }
  }

  /**
   * Open the entry modal to edit an existing entry
   */
  async editEntry(entry: any) {
    const modal = await this.modalController.create({
      component: EntryModalComponent,
      componentProps: {
        entry: entry,
        scheduleID: this.scheduleID
      },
      cssClass: 'modal-with-backdrop'
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'save' && data && Array.isArray(data) && data.length > 0) {
      await this.loadingService.enable();
      try {
        for (const entryData of data) {
          if (entryData.entryID) {
            await this.workScheduleService.handleUpdateSubjectEntry(entryData);
          }
        }
        await this.loadEntries();
        this.onChanged.emit(this.entries);
      } finally {
        this.loadingService.disable();
      }
    }
  }

  /**
   * Show delete confirmation for an entry
   */
  deleteEntry(entry: any) {
    this.entryToDelete = entry;
    this.popoverService.show(this.deletePopoverConfig);
  }

  /**
   * Actually delete the entry after confirmation
   */
  private async confirmDeleteEntry() {
    if (!this.entryToDelete) return;

    await this.loadingService.enable();
    try {
      await this.workScheduleService.handleDeleteSubjectEntry(this.entryToDelete.entryID);
      await this.loadEntries();
      this.onChanged.emit(this.entries);
    } finally {
      this.loadingService.disable();
      this.entryToDelete = null;
    }
  }

  /**
   * Get description text for an entry
   */
  getEntryDescription(entry: any): string {
    // Handle grouped entries with multiple days
    if (entry.daysOfWeek && entry.daysOfWeek.length > 0) {
      return this.formatDaysDescription(entry.daysOfWeek);
    } else if (entry.dayOfWeek !== null && entry.dayOfWeek !== undefined) {
      return this.translate.instant('WORK_SCHEDULE.' + this.daysOfWeek[entry.dayOfWeek]);
    } else if (entry.startDate) {
      // Date range entry
      let dateStr = entry.startDate;
      if (entry.endDate && entry.endDate !== entry.startDate) {
        dateStr += ' - ' + entry.endDate;
      }
      const suffix = entry.repeatAnnually ? ` (${this.translate.instant('WORK_SCHEDULE.Repeat_Annually')})` : '';
      return dateStr + suffix;
    } else if (entry.relativeDayName) {
      const offset = entry.relativeDayOffset ? ` (${entry.relativeDayOffset > 0 ? '+' : ''}${entry.relativeDayOffset})` : '';
      const suffix = entry.repeatAnnually ? ` (${this.translate.instant('WORK_SCHEDULE.Repeat_Annually')})` : '';
      return entry.relativeDayName + offset + suffix;
    }
    return '';
  }

  /**
   * Format array of day numbers into readable description
   */
  private formatDaysDescription(days: number[]): string {
    if (days.length === 0) return '';
    if (days.length === 1) {
      return this.translate.instant('WORK_SCHEDULE.' + this.daysOfWeek[days[0]]);
    }

    // Check for common patterns
    const isWeekdays = days.length === 5 && [1, 2, 3, 4, 5].every(d => days.includes(d));
    const isWeekend = days.length === 2 && days.includes(0) && days.includes(6);
    const isAllDays = days.length === 7;

    if (isAllDays) {
      return this.translate.instant('WORK_SCHEDULE.All_Days');
    }
    if (isWeekdays) {
      return this.translate.instant('WORK_SCHEDULE.Weekdays');
    }
    if (isWeekend) {
      return this.translate.instant('WORK_SCHEDULE.Weekend');
    }

    // Check if consecutive
    const sorted = [...days].sort((a, b) => a - b);
    let isConsecutive = true;
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] !== sorted[i - 1] + 1) {
        isConsecutive = false;
        break;
      }
    }

    if (isConsecutive && sorted.length > 2) {
      // Show as range: "Mon-Thu"
      const first = this.daysOfWeekShort[sorted[0]];
      const last = this.daysOfWeekShort[sorted[sorted.length - 1]];
      return `${first}-${last}`;
    }

    // Show as list: "Mon, Wed, Fri"
    return sorted.map(d => this.daysOfWeekShort[d]).join(', ');
  }

  /**
   * Get time range text for an entry
   */
  getEntryTimeRange(entry: any): string {
    // Format time as HH:MM (remove seconds if present)
    const formatTime = (time: string) => {
      if (!time) return '';
      const parts = time.split(':');
      return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
    };

    // Check if this is an "entire day" entry
    const startTime = formatTime(entry.startTime);
    const endTime = formatTime(entry.endTime);
    if (startTime === '00:00' && endTime === '23:59') {
      return `(${this.translate.instant('WORK_SCHEDULE.Entire_Day')})`;
    }

    return `${startTime} - ${endTime}`;
  }
}
